'use client';

import { faPaperPlane } from '@fortawesome/free-regular-svg-icons';
import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePersistentState } from '~/hooks';
import { Button, buttonClassNames } from '../Button';
import { StyledInput } from '../Form';
import { renderMarkdown } from './RenderText';

type Message = { role: 'user' | 'model'; text: string };
type HistoryItem = { userQuestion: string; AIResponse: string };

const MAX_QUESTIONS_PER_SESSION = 50;
const API_URL = (process.env.NEXT_PUBLIC_BACKEND_HOST || '') + 'ai/salesChat';

const getSessionCount = (): number => {
    if (typeof window === 'undefined') return 0;
    const raw = sessionStorage.getItem('chat_count');
    return raw ? parseInt(raw, 10) || 0 : 0;
};

const createConversationId = () => uuidv4();

export const Chatbot = () => {
    const [isMounted, setIsMounted] = useState(false);
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const conversationIdRef = useRef<string | null>(null);

    const [messages, setMessages] = usePersistentState<Message[]>('chat_messages');
    const [count, setCount] = useState(0);

    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setIsMounted(true);
        setCount(getSessionCount());
    }, []);

    useEffect(() => {
        if (!isMounted) return;
        sessionStorage.setItem('chat_messages', JSON.stringify(messages));
    }, [messages, isMounted]);

    useEffect(() => {
        if (!isMounted) return;
        sessionStorage.setItem('chat_count', String(count));
    }, [count, isMounted]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
            }, 100);
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [messages, open, loading]);

    const remaining = useMemo(() => Math.max(0, MAX_QUESTIONS_PER_SESSION - count), [count]);
    const limitReached = remaining <= 0;

    const exampleQuestions = [
        'Ich will Anwesenheiten mobil erfassen, ohne Zettelwirtschaft.',
        'Welches Modul hilft bei Online-Anmeldungen für Turniere?',
        'Ich brauche ein System für Online-Zahlungen bei Platzreservierungen.',
        'Welche Zahlungsmethoden unterstützt Sportision?'
    ];

    function clearHistory() {
        setMessages([]);
        setInput('');
        setLoading(false);
        setCount(0);
        conversationIdRef.current = createConversationId();

        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('chat_messages');
            sessionStorage.setItem('chat_count', '0');
        }
    }

    async function ask(overrideInput?: string) {
        const question = (overrideInput || input).trim();
        if (!question || limitReached || loading) return;

        setLoading(true);
        setInput('');

        const newMsg: Message = { role: 'user', text: question };
        const updatedMessages = [...(messages || []), newMsg];
        setMessages(updatedMessages);

        try {
            const historyPayload: HistoryItem[] = [];
            for (let i = 0; i < updatedMessages.length - 1; i++) {
                if (updatedMessages[i].role === 'user' && updatedMessages[i + 1]?.role === 'model') {
                    historyPayload.push({
                        userQuestion: updatedMessages[i].text,
                        AIResponse: updatedMessages[i + 1].text
                    });
                }
            }

            const res = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    prompt: question,
                    history: JSON.stringify(historyPayload),
                    conversationId: conversationIdRef.current
                })
            });

            const data = await res.json();

            if (!res.ok || data.error) {
                setMessages((m) => [
                    ...m,
                    {
                        role: 'model',
                        text: 'Es tut mir leid, ich konnte deine Frage nicht verarbeiten. Bitte wende dich für technische Unterstützung an unseren Support unter support@sportision.de oder rufe uns unter 030 12345678 an.'
                    }
                ]);
            } else {
                setMessages((m) => [...m, { role: 'model', text: data.answer }]);
                setCount((prev) => prev + 1);
            }
        } catch (error) {
            console.error(error);
            setMessages((m) => [
                ...m,
                {
                    role: 'model',
                    text: 'Ich kann gerade keine Verbindung herstellen. Für dringende Anfragen zu Buchungen, Trainingszeiten oder persönlichen Daten wende dich bitte direkt an deinen Verein. Bei Fragen zu Sportision kontaktiere uns unter support@sportision.de.'
                }
            ]);
        } finally {
            setLoading(false);
        }
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void ask();
        }
    }

    if (!isMounted) return null;

    return (
        <>
            <motion.button
                onClick={() => {
                    if (conversationIdRef.current === null) conversationIdRef.current = createConversationId();
                    setOpen((v) => !v);
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="tw-fixed tw-bottom-6 tw-right-6 tw-z-[9999] tw-flex tw-w-[155px] tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-white/20 tw-bg-gradient-to-br tw-from-success tw-to-success/90 tw-px-5 tw-py-3 tw-font-semibold tw-text-white tw-shadow-lg tw-backdrop-blur-sm"
            >
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={open ? 'close' : 'start'}
                        initial={{ opacity: 0, y: 6, filter: 'blur(4px)' }}
                        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, y: -6, filter: 'blur(4px)' }}
                        transition={{ duration: 0.18 }}
                        className="tw-inline-flex tw-items-center tw-gap-1"
                    >
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            {open ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                />
                            )}
                        </svg>
                        {open ? 'Schließen' : 'Chat starten'}
                    </motion.span>
                </AnimatePresence>
            </motion.button>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                            className="tw-pointer-events-none tw-fixed tw-inset-0 tw-z-[9998] tw-bg-black/30 tw-backdrop-blur-sm md:tw-hidden"
                        />

                        <motion.div
                            initial={{ opacity: 0, y: 320, x: 300, scale: 0 }}
                            animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 320, x: 300, scale: 0.0 }}
                            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                            className="tw-card-bordered tw-fixed tw-bottom-24 tw-right-2 tw-top-2 tw-z-[9999] tw-flex tw-w-[calc(100%-1rem)] tw-max-w-[600px] tw-flex-col tw-overflow-hidden tw-p-2 tw-shadow-xl tw-backdrop-blur-xl md:tw-right-6 md:tw-top-auto md:tw-h-[70vh] md:tw-max-h-[70vh] md:tw-px-4"
                        >
                            <div className="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-4 tw-backdrop-blur-md">
                                <div className="tw-flex tw-flex-col">
                                    <span className="tw-font-bold tw-text-accents-9">Sportision Assistant</span>
                                    <span className="tw-text-xs tw-text-accents-6">
                                        Ich beantworte deine Fragen zu unseren Software-Lösungen
                                    </span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <Button
                                        flat
                                        size="xs"
                                        onClick={clearHistory}
                                        disabled={loading || messages?.length === 0}
                                        className="!tw-h-8 !tw-rounded-full"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>

                                    <Button
                                        color="secondary"
                                        onClick={() => setOpen(false)}
                                        flat
                                        size="xs"
                                        className="!tw-h-8 !tw-w-8 !tw-rounded-full !tw-p-0"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                </div>
                            </div>
                            <div className="tw-card-bordered tw-rounded-xs tw-relative tw-flex-1 tw-overflow-hidden tw-p-0">
                                <div
                                    ref={listRef}
                                    className="tw-h-full tw-w-full tw-space-y-6 tw-overflow-y-auto tw-p-5"
                                >
                                    {(!messages || messages.length === 0) && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="tw-space-y-4 tw-text-center tw-text-accents-7"
                                        >
                                            <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-success/10 tw-text-3xl">
                                                <motion.span
                                                    animate={{
                                                        rotate: [0, 15, -10, 15, -4, 0]
                                                    }}
                                                    transition={{
                                                        duration: 1,
                                                        ease: 'easeInOut',
                                                        repeat: Infinity,
                                                        repeatDelay: 5
                                                    }}
                                                    style={{ transformOrigin: '70% 70%' }}
                                                >
                                                    👋
                                                </motion.span>
                                            </div>
                                            <div className="tw-mx-auto tw-text-xs lg:tw-max-w-[400px]">
                                                <p className="tw-my-0">
                                                    Hallo! Ich bin der Sportision Assistant und helfe bei Fragen zu
                                                    unseren Software-Lösungen für Sportschulen und -vereine.
                                                </p>
                                                <p className="tw-mt-2">
                                                    <strong>Hinweis:</strong> Keine Auskünfte zu Buchungen,
                                                    Trainingszeiten oder persönlichen Daten, bitte direkt an deinen
                                                    Verein wenden.
                                                </p>
                                            </div>
                                            <div className="tw-pt-2 tw-text-left">
                                                <p className="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-accents-5">
                                                    Beispielfragen zu Sportision
                                                </p>
                                                <div className="tw-grid tw-gap-2">
                                                    {exampleQuestions.map((q, i) => (
                                                        <motion.button
                                                            key={i}
                                                            whileHover={{ scale: 1.02, x: 2 }}
                                                            className={buttonClassNames({
                                                                flat: true,
                                                                size: 'sm'
                                                            })}
                                                            onClick={() => ask(q)}
                                                        >
                                                            <span className="tw-truncate">{q}</span>
                                                        </motion.button>
                                                    ))}
                                                </div>
                                            </div>
                                            <p className="tw-pt-2 tw-text-[10px] tw-text-accents-6">
                                                KI kann Fehler machen. Bitte prüfen Sie wichtige Infos.
                                            </p>
                                        </motion.div>
                                    )}

                                    {messages?.map((m, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            className={`tw-flex ${m.role === 'user' ? 'tw-justify-end' : 'tw-justify-start'}`}
                                        >
                                            <div
                                                className={`tw-max-w-[85%] tw-whitespace-pre-wrap tw-rounded-2xl tw-px-3 tw-py-2 tw-text-sm tw-shadow-sm md:tw-text-base ${
                                                    m.role === 'user'
                                                        ? 'tw-rounded-br-sm tw-bg-gradient-to-br tw-from-success tw-to-success/90 tw-text-white'
                                                        : 'tw-rounded-bl-sm tw-bg-white tw-text-accents-8 tw-ring-1 tw-ring-black/5'
                                                }`}
                                            >
                                                {renderMarkdown(m.text)}
                                            </div>
                                        </motion.div>
                                    ))}

                                    {loading && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="tw-flex tw-justify-start"
                                        >
                                            <div className="tw-rounded-2xl tw-rounded-bl-sm tw-bg-white tw-px-5 tw-py-4 tw-ring-1 tw-ring-black/5">
                                                <div className="tw-flex tw-gap-1.5">
                                                    <span className="tw-h-2 tw-w-2 tw-animate-bounce tw-rounded-full tw-bg-success/50"></span>
                                                    <span className="tw-h-2 tw-w-2 tw-animate-bounce tw-rounded-full tw-bg-success/50 [animation-delay:0.2s]"></span>
                                                    <span className="tw-h-2 tw-w-2 tw-animate-bounce tw-rounded-full tw-bg-success/50 [animation-delay:0.4s]"></span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                            <div className="tw-backdrop-blur-m tw-mt-1 tw-bg-white/60">
                                <div className="tw-relative tw-flex tw-items-center">
                                    <StyledInput
                                        ref={inputRef}
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={onKeyDown}
                                        disabled={loading || limitReached}
                                        placeholder={
                                            limitReached
                                                ? 'Limit erreicht'
                                                : 'Stelle eine Frage zur Sportision-Projekt...'
                                        }
                                        className="tw-w-full"
                                    />
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        onClick={() => ask()}
                                        disabled={loading || limitReached || !input.trim()}
                                        className="ml-2"
                                    >
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                    </Button>
                                </div>
                                <div className="tw-mt-1 tw-text-center tw-text-[10px] tw-text-accents-6">
                                    {limitReached ? (
                                        <span className="tw-text-error">
                                            Limit erreicht ({MAX_QUESTIONS_PER_SESSION} Fragen pro Sitzung).
                                            <br />
                                            Bei weiteren Fragen kontaktiere support@sportision.de
                                        </span>
                                    ) : (
                                        <span>Powered by Sportision</span>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};
